/**
 * Загрузка фото.
 *
 * Сжимаем на клиенте до 1600px перед отправкой: няня фотографирует на
 * телефон, оригинал весит 5–8 МБ, и в метро он не уйдёт никогда. После
 * сжатия — 200–400 КБ, и загрузка проходит даже на слабой сети.
 */

import { ApiError } from '@/shared/api/client'
import { getInitData } from '@/shared/telegram'

const MAX_SIDE = 1600
const QUALITY = 0.82

export interface UploadedMedia {
  id: number
  url: string
  thumb_url: string
  width?: number | null
  height?: number | null
}

async function compress(file: File): Promise<Blob> {
  // HEIC и прочие экзотические форматы браузер может не декодировать —
  // тогда отправляем оригинал, сожмёт сервер
  const bitmap = await createImageBitmap(file).catch(() => null)
  if (!bitmap) return file

  const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height))
  const width = Math.round(bitmap.width * scale)
  const height = Math.round(bitmap.height * scale)

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  if (!context) return file
  context.drawImage(bitmap, 0, 0, width, height)
  bitmap.close()

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', QUALITY),
  )
  return blob ?? file
}

export async function uploadPhoto(file: File): Promise<UploadedMedia> {
  const compressed = await compress(file)
  const form = new FormData()
  form.append('file', compressed, 'photo.jpg')

  const response = await fetch('/api/media', {
    method: 'POST',
    headers: { Authorization: `tma ${getInitData()}` },
    body: form,
  })

  if (!response.ok) {
    try {
      const data = (await response.json()) as { error?: { code: string; message: string } }
      if (data.error) throw new ApiError(response.status, data.error)
    } catch (error) {
      if (error instanceof ApiError) throw error
    }
    throw new ApiError(response.status, {
      code: 'upload_failed',
      message: 'Фото не загрузилось. Попробуйте ещё раз',
    })
  }

  return (await response.json()) as UploadedMedia
}
